import React, { useState } from 'react';
import { HSLColor, hslToHex, hslToRgb, PREVIEW_LIGHTNESS_STEPS } from '../utils/colorUtils';
import { Copy, Check, User } from 'lucide-react';
interface ColorPreviewProps {
  selectedColor: HSLColor;
  baseColor: HSLColor;
}
export function ColorPreview({
  selectedColor,
  baseColor
}: ColorPreviewProps) {
  const {
    h,
    s,
    l
  } = selectedColor;
  const hex = hslToHex(h, s, l);
  const rgb = hslToRgb(h, s, l);
  const cssVar = `${h} ${s}% ${l}%`;
  const [copied, setCopied] = useState(false);
  const copyToClipboard = () => {
    navigator.clipboard.writeText(cssVar);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  // Helper to determine text color based on lightness
  const getTextColor = (lightness: number) => lightness > 45 ? '#000000' : '#FFFFFF';
  // Extended lightness steps from #ffffd5 to #191b0c
  const lightnessSteps = PREVIEW_LIGHTNESS_STEPS;
  // Badge spectrum with more steps
  const badgeLightnessSteps = [95, 90, 85, 80, 75, 70, 65, 60, 55, 50, 45, 40, 35, 30, 25, 20, 15, 10];
  const hueSteps = [-15, -10, -5, 0, 5, 10, 15, 20];
  // Sparkline path generator
  const sparklinePath = 'M0,20 C10,18 20,22 30,15 C40,8 50,12 60,18 C70,24 80,10 90,15 C100,20 110,5 120,10';
  // Calculate approximate contrast ratio against dark background (simplified)
  const getContrastRatio = (lightness: number): number => {
    // Approximate luminance calculation
    const L1 = lightness / 100;
    const L2 = 0.05; // dark background ~5% luminance
    return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
  };
  return <div className="fixed bottom-0 right-0 w-full md:w-[480px] bg-card border-t md:border-l border-[hsl(var(--border)/0.2)] shadow-2xl p-6 overflow-y-auto max-h-[50vh] md:max-h-screen z-40" data-id="element-108">
      <div className="space-y-8" data-id="element-109">
        {/* Header Section */}
        <div data-id="element-110">
          <h3 className="text-lg font-semibold mb-1" data-id="element-111">Selected Color</h3>
          <div className="flex items-center gap-4 mb-4" data-id="element-112">
            <div className="w-16 h-16 rounded-lg shadow-inner border border-[hsl(var(--border)/0.1)]" style={{
            backgroundColor: `hsl(${h}, ${s}%, ${l}%)`
          }} data-id="element-113" />
            <div className="flex-1 space-y-1" data-id="element-114">
              <code className="text-sm font-mono bg-muted px-2 py-0.5 rounded" data-id="element-115">
                {hex}
              </code>
              <div className="text-xs text-muted-foreground font-mono" data-id="element-116">
                {rgb}
              </div>
              <div className="text-xs text-muted-foreground font-mono" data-id="element-117">
                HSL: {h}, {s}%, {l}%
              </div>
            </div>
          </div>

          <button onClick={copyToClipboard} className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-secondary hover:bg-secondary/80 rounded-md text-sm font-medium transition-colors border border-[hsl(var(--border)/0.1)]" data-id="element-118">
            {copied ? <Check className="w-4 h-4" data-id="element-119" /> : <Copy className="w-4 h-4" data-id="element-120" />}
            {copied ? 'Copied CSS Variable' : 'Copy CSS Variable'}
          </button>
        </div>

        <div className="h-px bg-[hsl(var(--border)/0.1)]" data-id="element-121" />

        {/* Color Spectrum Row (like top of reference) */}
        <div className="space-y-3" data-id="element-122">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-123">
            Full Spectrum (#ffffd5 → #191b0c)
          </h4>
          <div className="flex gap-1" data-id="element-124">
            {lightnessSteps.map(stepL => <div key={stepL} className="flex-1 aspect-square rounded-md shadow-sm" style={{
            backgroundColor: `hsl(${h}, ${s}%, ${stepL}%)`
          }} title={hslToHex(h, s, stepL)} data-id="element-125" />)}
          </div>
        </div>

        {/* Badge Spectrum Row */}
        <div className="space-y-3" data-id="element-126">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-127">
            Badge Spectrum
          </h4>
          <div className="flex flex-wrap gap-1.5" data-id="element-128">
            {badgeLightnessSteps.map(stepL => <div key={stepL} className="px-2 py-0.5 rounded text-[9px] font-medium shadow-sm" style={{
            backgroundColor: `hsl(${h}, ${s}%, ${stepL}%)`,
            color: getTextColor(stepL)
          }} data-id="element-129">
                Badge
              </div>)}
          </div>
        </div>

        {/* Contrast Analysis Bars */}
        <div className="space-y-3" data-id="element-130">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-131">
            Contrast Analysis
          </h4>
          <div className="rounded-xl overflow-hidden border border-[hsl(var(--border)/0.1)]" data-id="element-132">
            {lightnessSteps.map(stepL => {
            const contrast = getContrastRatio(stepL);
            const textColor = getTextColor(stepL);
            const stepHex = hslToHex(h, s, stepL);
            const passesAA = contrast >= 4.5;
            const passesAAA = contrast >= 7;
            const passesAALarge = contrast >= 3;
            return <div key={stepL} className="flex items-center justify-between px-4 py-2.5 text-xs font-mono" style={{
              backgroundColor: `hsl(${h}, ${s}%, ${stepL}%)`,
              color: textColor
            }} data-id="element-133">
                  <div className="flex items-center gap-3" data-id="element-134">
                    <span className="w-16" data-id="element-135">{stepHex}</span>
                    <div className="flex gap-1" data-id="element-136">
                      {[passesAALarge, passesAA, passesAAA, contrast >= 10, contrast >= 15, contrast >= 18].map((passes, i) => <div key={i} className={`w-2 h-2 rounded-full transition-colors ${passes ? textColor === '#000000' ? 'bg-black/80' : 'bg-white/80' : textColor === '#000000' ? 'bg-black/20' : 'bg-white/20'}`} data-id="element-137" />)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-138">
                    <span className="opacity-60 text-[10px]" data-id="element-139">{stepL}%</span>
                    <span className="w-12 text-right" data-id="element-140">
                      {contrast.toFixed(2)}
                    </span>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Gradient Blocks */}
        <div className="space-y-3" data-id="element-141">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-142">
            Gradient Context
          </h4>
          <div className="grid grid-cols-4 gap-2 h-24" data-id="element-143">
            <div className="rounded-lg shadow-sm" style={{
            backgroundColor: `hsl(${h}, ${s}%, ${l}%)`
          }} data-id="element-144" />
            <div className="rounded-lg shadow-sm" style={{
            background: `linear-gradient(to bottom, hsl(${h}, ${s}%, 92%), hsl(${h}, ${s}%, 8%))`
          }} data-id="element-145" />
            <div className="rounded-lg shadow-sm" style={{
            background: `linear-gradient(135deg, hsl(${h}, ${s}%, 75%), hsl(${h}, ${s}%, 23%))`
          }} data-id="element-146" />
            <div className="rounded-lg shadow-sm" style={{
            background: `linear-gradient(to right, hsl(${h}, ${s}%, 8%), hsl(${h}, ${s}%, 92%))`
          }} data-id="element-147" />
          </div>
        </div>

        {/* Indicator Row */}
        <div className="space-y-3" data-id="element-148">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-149">
            Indicators
          </h4>
          <div className="flex justify-between items-end px-2" data-id="element-150">
            {[100, 80, 60, 40, 20].map((opacity, i) => <div key={i} className="flex flex-col items-center gap-2" data-id="element-151">
                <div className="w-3 h-3 rounded-full" style={{
              backgroundColor: `hsl(${h}, ${s}%, ${l}%, ${opacity / 100})`
            }} data-id="element-152" />
                <span className="text-[10px] text-muted-foreground" data-id="element-153">Ind</span>
              </div>)}
            <div className="flex flex-col items-center gap-2" data-id="element-154">
              <div className="w-3 h-3 rounded-full border-2" style={{
              borderColor: `hsl(${h}, ${s}%, ${l}%)`
            }} data-id="element-155" />
              <span className="text-[10px] text-muted-foreground" data-id="element-156">Out</span>
            </div>
          </div>
        </div>

        {/* Avatar Row */}
        <div className="space-y-3" data-id="element-157">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-158">
            Avatars
          </h4>
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" data-id="element-159">
            {hueSteps.map((hueStep, i) => <div key={i} className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center shadow-sm border border-[hsl(var(--border)/0.05)]" style={{
            backgroundColor: `hsl(${h + hueStep}, ${s}%, ${l}%)`
          }} data-id="element-160">
                <User className="w-5 h-5 opacity-80" color={getTextColor(l)} data-id="element-161" />
              </div>)}
          </div>
        </div>

        {/* Stat Cards */}
        <div className="space-y-3" data-id="element-162">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-163">
            Data Visualization
          </h4>
          <div className="grid grid-cols-2 gap-3" data-id="element-164">
            {[1, 2].map(i => <div key={i} className="bg-card border border-[hsl(var(--border)/0.1)] rounded-xl p-4 shadow-sm" data-id="element-165">
                <div className="text-[10px] text-muted-foreground mb-1" data-id="element-166">
                  Label
                </div>
                <div className="text-2xl font-bold mb-2" data-id="element-167">9,173</div>
                <svg viewBox="0 0 120 30" className="w-full h-8 overflow-visible" data-id="element-168">
                  <path d={sparklinePath} fill="none" stroke={`hsl(${h}, ${s}%, ${l}%)`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-169" />
                  <path d={`${sparklinePath} L120,40 L0,40 Z`} fill={`hsl(${h}, ${s}%, ${l}%)`} fillOpacity="0.1" stroke="none" data-id="element-170" />
                </svg>
              </div>)}
          </div>
        </div>

        <div className="h-px bg-[hsl(var(--border)/0.1)]" data-id="element-171" />

        {/* Comparison */}
        <div data-id="element-172">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3" data-id="element-173">
            Comparison
          </h4>
          <div className="grid grid-cols-2 gap-3 text-center text-xs" data-id="element-174">
            <div className="space-y-2" data-id="element-175">
              <div className="h-16 rounded-lg w-full shadow-sm border border-[hsl(var(--border)/0.05)]" style={{
              backgroundColor: `hsl(${baseColor.h}, ${baseColor.s}%, ${baseColor.l}%)`
            }} data-id="element-176" />
              <span className="text-muted-foreground font-mono" data-id="element-177">Current</span>
            </div>
            <div className="space-y-2" data-id="element-178">
              <div className="h-16 rounded-lg w-full shadow-sm border border-[hsl(var(--border)/0.05)]" style={{
              backgroundColor: `hsl(${h}, ${s}%, ${l}%)`
            }} data-id="element-179" />
              <span className="text-muted-foreground font-mono" data-id="element-180">Selected</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}